// @flow
import React from 'react';
import classnames from 'classnames';
import { PlatformSwitch, SettingsMenu, AppMobileFooter, AppMobileHeader, BlockedFeature } from '@blocks';
import { Button, Spinner, Icon } from '@elements';
import type { User, BundledPermissions, GoogleCalendarState } from '@types';
import { useTranslationNs } from '@helpers/helpers';
import './GoogleCalendar.scss';

type Props = {
  user: User,
  permissions: BundledPermissions,
  onLogout: Function,
  status: ?GoogleCalendarState,
  // The page to sign into Google was opened: the user is told to come back here
  opened: boolean,
  onConnect: Function,
  onDisconnect: Function,
  onReload: Function,
  // "Fazer Upgrade" in the paywall, before it goes to the plans
  onUpgrade: Function,
};

// Ajustes › Google Agenda: what connecting does, who is connected, and the button to connect or disconnect.
// Connecting happens in the browser, on the API's page (GoogleCalendarContainer opens it). On a plan
// without it, the same points and the plans that include it instead of the button.
const GoogleCalendar = ({
  user, permissions, onLogout, status, opened, onConnect, onDisconnect, onReload, onUpgrade,
}: Props) => {
  const [_] = useTranslationNs('settings');

  const loaded = Boolean(status && status.lastFetch);
  const connected = Boolean(status && status.connected);
  const email = status && status.email;
  const upgradeRequired = Boolean(status && status.upgradeRequired);
  const canUpgrade = Boolean(status && status.canUpgrade);

  const points = [
    { icon: 'event_available', text: _('google_calendar/point/calendar') },
    { icon: 'videocam', text: _('google_calendar/point/meet') },
    { icon: 'privacy', text: _('google_calendar/point/privacy') },
  ];

  const statusCard = () => (
    <div className="googleCalendar-card">
      <div className="googleCalendar-cardHeader">
        <span className={classnames('googleCalendar-statusIcon', { connected })}>
          <Icon iconName={connected ? 'check-circle' : 'event_available'} size={26} />
        </span>
        <div className="googleCalendar-cardTitle" role="status">
          {connected ? (
            <React.Fragment>
              <h2>{email ? _('google_calendar/connected_as') : _('google_calendar/connected')}</h2>
              {email && ' '}
              {email && <p className="googleCalendar-account">{email}</p>}
            </React.Fragment>
          ) : (
            <h2>{_('google_calendar/not_connected')}</h2>
          )}
        </div>
      </div>
      {connected && upgradeRequired && <p className="googleCalendar-paused">{_('google_calendar/paywall/paused')}</p>}
      {status && status.disconnectError && <p className="googleCalendar-error" role="alert">{status.disconnectError}</p>}
      {!connected && opened && <p className="googleCalendar-opened">{_('google_calendar/opened')}</p>}
      <div className="googleCalendar-cardActions">
        {connected ? (
          <Button
            className="dangerOutline googleCalendar-disconnect"
            onClick={onDisconnect}
            isLoading={Boolean(status && status.isDisconnecting)}
            greenSpinner
            loadingMessage={_('google_calendar/disconnect')}
          >
            {_('google_calendar/disconnect')}
          </Button>
        ) : (
          status && status.canConnect && (
            <Button className="primary googleCalendar-connect" onClick={onConnect}>
              {_('google_calendar/connect')}
            </Button>
          )
        )}
      </div>
    </div>
  );

  // The app's paywall (BlockedFeature): who owns the plan gets "Fazer Upgrade", the others ask them
  const paywall = () => (
    <BlockedFeature
      simpleStyle
      className="googleCalendar-paywall"
      iconName="lock"
      iconSize={240}
      customMessage={_('google_calendar/paywall/title')}
      hint={_(canUpgrade ? 'google_calendar/paywall/upgrade' : 'google_calendar/paywall/ask_owner')}
      hideUpgradeButton={!canUpgrade}
      onUpgrade={onUpgrade}
    />
  );

  const content = () => {
    if (!loaded && status && status.error) {
      return (
        <div className="googleCalendar-status">
          <p className="googleCalendar-error" role="alert">{_('google_calendar/error/load')}</p>
          <Button onClick={onReload}>{_('google_calendar/retry')}</Button>
        </div>
      );
    }
    if (!loaded) return <div className="googleCalendar-loading"><Spinner /></div>;

    return (
      <React.Fragment>
        <ul className="googleCalendar-points">
          {points.map(point => (
            <li key={point.icon}>
              <Icon iconName={point.icon} size={20} />
              <span>{point.text}</span>
            </li>
          ))}
        </ul>
        <div className="googleCalendar-status">
          {upgradeRequired && paywall()}
          {/* Connected before the plan changed: they can still disconnect */}
          {(!upgradeRequired || connected) && statusCard()}
        </div>
      </React.Fragment>
    );
  };

  return (
    <PlatformSwitch
      inject
      desktop={() => (
        <div className="appContent settingsPage googleCalendarSettings">
          <aside className="sidebar">
            <SettingsMenu user={user} onLogout={onLogout} permissions={permissions} />
          </aside>
          <section className="mainSection">
            <div className="centerContent">
              <div className="toolbar dashed">
                <h1>{_('google_calendar/title')}</h1>
              </div>
              {content()}
            </div>
          </section>
        </div>
      )}
      mobile={() => (
        <React.Fragment>
          <AppMobileHeader title={_('google_calendar/title')} backLinkTo="/ajustes" />
          <div className="appContent settingsPage googleCalendarSettings">
            <section className="mainSection">
              <div className="centerContent">
                {content()}
              </div>
            </section>
          </div>
          <AppMobileFooter />
        </React.Fragment>
      )}
    />
  );
};

export default GoogleCalendar;
